/**
 * Types for the spreadsheet import path. Field names mirror the pymble-apis
 * models in services/group/models/resolve.py exactly - there is no generated
 * client, so any rename must be made in both places.
 *
 * Server-side caps mirrored here so the client can reject an oversized
 * submission before burning a network round trip (the API enforces them
 * again regardless): the values list is capped at MAX_SUBMITTED_VALUES and
 * each value at MAX_VALUE_LENGTHS[identifier_type] characters.
 */

export type IdentifierType = "email" | "edumate_contact_id" | "student_number";
export type ColumnRole = "contact" | "student";

/** Mirrors MAX_SUBMITTED_VALUES in pymble-apis models/resolve.py. */
export const MAX_SUBMITTED_VALUES = 5000;

/** Mirrors MAX_VALUE_LENGTHS in pymble-apis models/resolve.py. */
export const MAX_VALUE_LENGTHS: Record<IdentifierType, number> = {
  email: 320,
  edumate_contact_id: 32,
  student_number: 64,
};

export interface ResolveContactsRequest {
  identifier_type: IdentifierType;
  role: ColumnRole;
  values: string[];
  mail_carers_only: boolean;
}

/**
 * One resolved entry. Several entries can share one `value`: a student row
 * that expanded to multiple carers arrives as one entry per carer
 * (apis#1117), so consumers must collect every entry's contact_id and never
 * assume one entry per submitted value.
 */
export interface ResolvedContact {
  value: string;
  contact_id: string;
  label: string;
}

export interface CandidateContact {
  contact_id: string;
  label: string;
}

export interface AmbiguousContact {
  value: string;
  candidates: CandidateContact[];
}

export interface ResolveContactsResponse {
  resolved: ResolvedContact[];
  ambiguous: AmbiguousContact[];
  inactive: ResolvedContact[];
  unmatched: string[];
  submitted_count: number;
  unique_contact_count: number;
  exceeds_individual_cap: boolean;
  max_individuals: number;
}
